/**
 * Multi-Venue Configuration System
 *
 * Provides venue-specific configuration for GPS parsing, validation, and river detection.
 * Enables the platform to support rowing venues worldwide by abstracting geographic assumptions.
 */

export interface VenueConfig {
  /** Display name of the venue */
  name: string;

  /** GPS reference point (typically the rowing club or start line) */
  referencePoint: {
    lat: number;
    lon: number;
  };

  /** Geographic bounds for GPS validation */
  validationBounds: {
    minLat: number;
    maxLat: number;
    minLon: number;
    maxLon: number;
  };

  /** Optional river detection configuration (venue-specific geography) */
  riverBoundary?: {
    /** Human-readable condition description */
    condition: string;
    /** Function to determine if GPS coordinates are on the river */
    logic: (lat: number, lon: number) => boolean;
  };
}

/**
 * Venue configurations for supported rowing locations
 */
export const VENUES: Record<string, VenueConfig> = {
  cambridge: {
    name: 'Cambridge, UK',
    referencePoint: {
      lat: 52.215207, // Cantabrigian Rowing Club
      lon: 0.144097,
    },
    validationBounds: {
      minLat: 52.0,
      maxLat: 52.3,
      minLon: 0.0,
      maxLon: 0.3,
    },
    riverBoundary: {
      condition: 'lat > 52.218 AND lon > 0.155',
      logic: (lat: number, lon: number): boolean => {
        // River Cam flows northeast of the rowing club
        // Higher coordinates indicate points closer to the river
        return lat > 52.218 && lon > 0.155;
      },
    },
  },

  princeton: {
    name: 'Princeton, NJ',
    referencePoint: {
      lat: 40.3573, // Princeton University area
      lon: -74.6672,
    },
    validationBounds: {
      minLat: 40.0,
      maxLat: 40.6,
      minLon: -75.0,
      maxLon: -74.0,
    },
    // No river boundary - different geography, all GPS points accepted
  },
};

/**
 * Get venue configuration by name, with fallback to Cambridge
 *
 * @param venueName - Name of the venue (e.g., 'cambridge', 'princeton')
 * @returns VenueConfig object for the specified venue
 */
export const getVenueConfig = (venueName: string): VenueConfig => {
  const venue = VENUES[venueName?.toLowerCase()];

  if (!venue) {
    console.warn(`Unknown venue: "${venueName}". Falling back to Cambridge configuration.`);
    return VENUES.cambridge;
  }

  return venue;
};

/**
 * Get list of all supported venue names
 */
export const getSupportedVenues = (): string[] => {
  return Object.keys(VENUES);
};

/**
 * Validate if coordinates are within a venue's bounds
 *
 * @param lat - Latitude coordinate
 * @param lon - Longitude coordinate
 * @param venueName - Name of the venue to validate against
 * @returns true if coordinates are within venue bounds
 */
export const isWithinVenueBounds = (lat: number, lon: number, venueName: string): boolean => {
  const venue = getVenueConfig(venueName);
  const bounds = venue.validationBounds;

  return (
    lat >= bounds.minLat && lat <= bounds.maxLat && lon >= bounds.minLon && lon <= bounds.maxLon
  );
};

/**
 * Check if coordinates are on the river for a given venue
 *
 * @param lat - Latitude coordinate
 * @param lon - Longitude coordinate
 * @param venueName - Name of the venue
 * @returns true if on river, or true if venue has no river boundary defined
 */
export const isOnRiver = (lat: number, lon: number, venueName: string): boolean => {
  const venue = getVenueConfig(venueName);

  if (!venue.riverBoundary) {
    // No river boundary defined - accept all points
    return true;
  }

  return venue.riverBoundary.logic(lat, lon);
};
